import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { createProduct, updateProduct, getProducts } from '../../Store/products/productsSlice';
import RichTextEditor from './RichTextEditor';
import ProductSelectModal from './ProductSelectModal';
import { BASE_URL, getSiteOrigin } from '../../utils/constants';
import styles from '../../styles/AdminPanel.module.scss';
import { assetUrl } from '../../utils/imageUrl';
import {
  normalizeWholesaleTiers,
  resolveWholesaleTiers,
  validateWholesaleTiers,
} from '../../utils/productPrice';
import {
  generateProductMetaTitle,
  generateProductSnippet,
  SNIPPET_LIMITS,
} from '../../utils/productSnippet';
import {
  normalizeStockLabel,
  STOCK_LABEL_OPTIONS,
} from '../../utils/productStockStatus';
import { getCategoryIndentLabel, getHierarchicalCategories } from '../../utils/categoryTree';

const SeoPreview = ({ title, url, description }) => (
  <div className={styles.categorySeoPreview}>
    <div className={styles.categorySeoPreviewUrl}>{url}</div>
    <div className={styles.categorySeoPreviewTitle}>{title || 'Заголовок сторінки'}</div>
    <div className={styles.categorySeoPreviewDesc}>
      {description || "Снипет для пошукових систем з'явиться тут."}
    </div>
  </div>
);

const MAX_IMAGES = 5;
const MAX_GIFTS = 5;
const MAX_RELATED = 10;
const EMPTY_TIER = { minQty: '', price: '' };
const EMPTY_ATTRIBUTE = { name: '', value: '' };

const ProductForm = ({ product, categories, products = [], initialCategoryId = null, onCancel, onSuccess, onSaved, showMessage }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    title: '',
    price: '',
    oldPrice: '',
    description: '',
    categoryId: '',
    article: '',
    stockLabel: 'ready_to_ship',
    quantity: '0',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
  });
  const [wholesaleTiers, setWholesaleTiers] = useState([{ ...EMPTY_TIER }]);
  const [attributes, setAttributes] = useState([{ ...EMPTY_ATTRIBUTE }]);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [giftProducts, setGiftProducts] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [isRelatedModalOpen, setIsRelatedModalOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [loadedProduct, setLoadedProduct] = useState(null);

  const categoryOptions = useMemo(
    () => getHierarchicalCategories(categories),
    [categories]
  );

  const applyProductToForm = (source) => {
    if (!source) return;
    setLoadedProduct(source);
    setFormData({
      title: source.title || '',
      price: source.price || '',
      oldPrice: source.oldPrice ?? '',
      description: typeof source.description === 'string' ? source.description : '',
      categoryId: source.category?.id || source.categoryId || '',
      article: source.article || '',
      stockLabel: normalizeStockLabel(source.stockLabel, source.inStock),
      quantity: String(source.quantity ?? 0),
      metaTitle: source.metaTitle || '',
      metaDescription: source.metaDescription || source.shortDescription || '',
      metaKeywords: source.metaKeywords || '',
    });
    const tiers = resolveWholesaleTiers(source);
    setWholesaleTiers(tiers.length ? tiers.map((t) => ({ minQty: String(t.minQty), price: String(t.price) })) : [{ ...EMPTY_TIER }]);
    const attrs = Array.isArray(source.attributes) ? source.attributes : [];
    setAttributes(attrs.length ? attrs.map((a) => ({ name: a.name, value: a.value })) : [{ ...EMPTY_ATTRIBUTE }]);
    setExistingImages(Array.isArray(source.images) ? [...source.images] : []);
    setNewImages([]);
    setPreviewIndex(0);
  };

  useEffect(() => {
    if (!loadedProduct || !products.length) return;
    const giftIds = loadedProduct.giftProductIds || [];
    setGiftProducts(
      giftIds
        .map((giftId) => products.find((p) => Number(p.id) === Number(giftId)))
        .filter(Boolean)
    );
    const relatedIds = loadedProduct.relatedProductIds || [];
    setRelatedProducts(
      relatedIds
        .map((relatedId) => products.find((p) => Number(p.id) === Number(relatedId)))
        .filter(Boolean)
    );
  }, [loadedProduct, products]);

  useEffect(() => {
    if (!product?.id) {
      setLoadedProduct(null);
      setFormData({
        title: '',
        price: '',
        oldPrice: '',
        description: '',
        categoryId: initialCategoryId ? String(initialCategoryId) : '',
        article: '',
        stockLabel: 'ready_to_ship',
        quantity: '0',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: '',
      });
      setWholesaleTiers([{ ...EMPTY_TIER }]);
      setAttributes([{ ...EMPTY_ATTRIBUTE }]);
      setExistingImages([]);
      setNewImages([]);
      setGiftProducts([]);
      setRelatedProducts([]);
      setPreviewIndex(0);
      return;
    }

    let cancelled = false;
    setIsLoadingProduct(true);

    axios.get(`${BASE_URL}/products/${product.id}`)
      .then((res) => {
        if (!cancelled) applyProductToForm(res.data);
      })
      .catch(() => {
        if (!cancelled) applyProductToForm(product);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingProduct(false);
      });

    return () => {
      cancelled = true;
    };
  }, [product?.id, initialCategoryId]);

  const allImageItems = [
    ...existingImages.map((src, index) => ({ type: 'existing', src, index })),
    ...newImages.map((file, index) => ({ type: 'new', file, index })),
  ];
  const totalImages = allImageItems.length;
  const slotsLeft = MAX_IMAGES - totalImages;

  const getPreviewSrc = (item) => {
    if (!item) return null;
    return item.type === 'existing' ? assetUrl(item.src) : URL.createObjectURL(item.file);
  };

  const handleAddImages = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (slotsLeft <= 0) {
      showMessage('error', `Можна завантажити не більше ${MAX_IMAGES} зображень`);
      return;
    }
    const filesToAdd = files.slice(0, slotsLeft);
    if (files.length > slotsLeft) {
      showMessage('error', `Додано ${filesToAdd.length} з ${files.length}. Максимум ${MAX_IMAGES} фото.`);
    }
    setNewImages((prev) => [...prev, ...filesToAdd]);
  };

  const removeImageItem = (globalIndex) => {
    const item = allImageItems[globalIndex];
    if (!item) return;
    if (item.type === 'existing') {
      setExistingImages((prev) => prev.filter((_, i) => i !== item.index));
    } else {
      setNewImages((prev) => prev.filter((_, i) => i !== item.index));
    }
    if (previewIndex >= totalImages - 1) {
      setPreviewIndex(Math.max(0, totalImages - 2));
    }
  };

  const addWholesaleTier = () => {
    if (wholesaleTiers.length >= 10) {
      showMessage('error', 'Максимум 10 оптових рівнів');
      return;
    }
    setWholesaleTiers((prev) => [...prev, { ...EMPTY_TIER }]);
  };

  const updateWholesaleTier = (index, field, value) => {
    setWholesaleTiers((prev) => prev.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)));
  };

  const removeWholesaleTier = (index) => {
    setWholesaleTiers((prev) => (prev.length === 1 ? [{ ...EMPTY_TIER }] : prev.filter((_, i) => i !== index)));
  };

  const addAttribute = () => {
    if (attributes.length >= 30) {
      showMessage('error', 'Максимум 30 характеристик');
      return;
    }
    setAttributes((prev) => [...prev, { ...EMPTY_ATTRIBUTE }]);
  };

  const updateAttribute = (index, field, value) => {
    setAttributes((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const removeAttribute = (index) => {
    setAttributes((prev) => (prev.length === 1 ? [{ ...EMPTY_ATTRIBUTE }] : prev.filter((_, i) => i !== index)));
  };

  const handleAddGift = (selectedProduct) => {
    if (giftProducts.length >= MAX_GIFTS) {
      showMessage('error', `Максимум ${MAX_GIFTS} подарунків`);
      return;
    }
    const fullProduct = products.find((p) => Number(p.id) === Number(selectedProduct.id)) || selectedProduct;
    setGiftProducts((prev) => [...prev, fullProduct]);
  };

  const removeGift = (index) => {
    setGiftProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddRelated = (selectedProduct) => {
    const selectedId = Number(selectedProduct.id);
    if (!selectedId) {
      showMessage('error', 'Не вдалося визначити товар');
      return;
    }
    if (product?.id && selectedId === Number(product.id)) {
      showMessage('error', 'Товар не можна додати супутнім самому собі');
      return;
    }
    if (relatedProducts.length >= MAX_RELATED) {
      showMessage('error', `Максимум ${MAX_RELATED} супутніх товарів`);
      return;
    }
    if (relatedProducts.some((item) => Number(item.id) === selectedId)) return;
    const fullProduct = products.find((p) => Number(p.id) === selectedId) || selectedProduct;
    setRelatedProducts((prev) => [...prev, fullProduct]);
  };

  const removeRelated = (relatedId) => {
    setRelatedProducts((prev) => prev.filter((item) => item.id !== relatedId));
  };

  const validateForm = () => {
    if (!formData.title || !formData.price || !formData.description || !formData.categoryId) {
      showMessage('error', "Заповніть обов'язкові поля");
      return false;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formData.description;
    if (!tempDiv.textContent || tempDiv.textContent.trim() === '') {
      showMessage('error', 'Заповніть опис товару');
      return false;
    }

    if (totalImages === 0) {
      showMessage('error', 'Завантажте хоча б одне зображення');
      return false;
    }

    const priceNum = Number(formData.price);
    const oldPriceNum = formData.oldPrice !== '' ? Number(formData.oldPrice) : null;
    if (oldPriceNum !== null && oldPriceNum <= priceNum) {
      showMessage('error', 'Стара ціна повинна бути вище поточної');
      return false;
    }

    const tierError = validateWholesaleTiers(wholesaleTiers, priceNum);
    if (tierError) {
      showMessage('error', tierError);
      return false;
    }

    for (const row of attributes) {
      const hasName = row.name.trim() !== '';
      const hasValue = row.value.trim() !== '';
      if (hasName !== hasValue) {
        showMessage('error', 'Заповніть назву та значення характеристики');
        return false;
      }
    }

    return true;
  };

  const submitForm = async (stayOnPage = false) => {
    showMessage('', '');
    if (!validateForm()) return;

    const filledTiers = normalizeWholesaleTiers(
      wholesaleTiers.filter((tier) => tier.minQty !== '' || tier.price !== '')
    );
    const filledAttributes = attributes
      .filter((row) => row.name.trim() && row.value.trim())
      .map((row) => ({ name: row.name.trim(), value: row.value.trim() }));

    const relatedIds = relatedProducts
      .map((item) => Number(item.id))
      .filter((id) => id > 0);

    if (product?.id && relatedIds.includes(Number(product.id))) {
      showMessage('error', 'Товар не можна додати супутнім самому собі');
      return;
    }

    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('price', formData.price);
      data.append('oldPrice', formData.oldPrice);
      data.append('shortDescription', formData.metaDescription || '');
      data.append('description', formData.description);
      data.append('categoryId', formData.categoryId);
      data.append('article', formData.article || '');
      data.append('stockLabel', formData.stockLabel);
      data.append('quantity', formData.quantity || '0');
      data.append('wholesalePrices', JSON.stringify(filledTiers));
      data.append('attributes', JSON.stringify(filledAttributes));
      data.append('metaTitle', formData.metaTitle || '');
      data.append('metaDescription', formData.metaDescription || '');
      data.append('metaKeywords', formData.metaKeywords || '');
      data.append('giftProductIds', JSON.stringify(giftProducts.map((item) => item.id)));
      data.append('relatedProductIds', JSON.stringify(relatedIds));

      if (product) {
        data.append('keepImages', JSON.stringify(existingImages));
      }

      newImages.forEach((image) => data.append('images', image));

      let savedProduct;
      if (product) {
        savedProduct = await dispatch(updateProduct({ id: product.id, formData: data })).unwrap();
        showMessage('success', 'Товар успішно оновлено!');
      } else {
        savedProduct = await dispatch(createProduct(data)).unwrap();
        showMessage('success', 'Товар успішно додано!');
      }

      dispatch(getProducts());
      if (savedProduct && typeof savedProduct === 'object' && savedProduct.id) {
        onSaved?.(savedProduct);
      }
      if (stayOnPage && product) {
        return;
      }
      onSuccess();
    } catch (error) {
      const message = typeof error === 'string'
        ? error
        : error?.message || 'Помилка при збереженні товару';
      showMessage('error', message);
      console.error(error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitForm(false);
  };

  const previewItem = allImageItems[previewIndex] ?? allImageItems[0];

  return (
    <div className={styles.productFormCard}>
      <form onSubmit={handleSubmit}>
        <div className={styles.productFormHeader}>
          <div>
            <h2>{product ? 'Редагування товару' : 'Створення товару'}</h2>
            {product?.id && <span>ID: {product.id}</span>}
          </div>
          <div className={styles.productFormHeaderActions}>
            <button type="button" className={styles.productFormCancelBtn} onClick={onCancel}>
              Скасувати
            </button>
            {product && (
              <button type="button" className={styles.productFormStayBtn} onClick={() => submitForm(true)}>
                Зберегти и остаться
              </button>
            )}
            <button type="submit" className={styles.productFormSaveBtn}>
              {product ? 'Зберегти' : 'Створити'}
            </button>
          </div>
        </div>

        <div className={styles.productFormBody}>
          <div className={styles.productFormMain}>
            <section className={styles.productFormSection}>
              <h3 className={styles.productFormSectionTitle}>Основне</h3>
              <div className={styles.productFormSectionContent}>
                <div className={styles.productFormField}>
                  <label htmlFor="productTitle">Назва товару *</label>
                  <input
                    type="text"
                    id="productTitle"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Введіть назву товару"
                    required
                  />
                </div>
                <div className={styles.productFormRow2}>
                  <div className={styles.productFormField}>
                    <label htmlFor="productArticle">Артикул / код</label>
                    <input
                      type="text"
                      id="productArticle"
                      value={formData.article}
                      onChange={(e) => setFormData({ ...formData, article: e.target.value })}
                      placeholder="Код товару"
                    />
                  </div>
                  <div className={styles.productFormField}>
                    <label htmlFor="productCategory">Категорія *</label>
                    <select
                      id="productCategory"
                      value={formData.categoryId}
                      onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                      required
                    >
                      <option value="">Оберіть категорію</option>
                      {categoryOptions.map((category) => (
                        <option key={category.id} value={category.id}>
                          {getCategoryIndentLabel(category.name, category.depth)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </section>

            <section className={styles.productFormSection}>
              <h3 className={styles.productFormSectionTitle}>Дані</h3>
              <div className={styles.productFormSectionContent}>
                <div className={styles.productFormRow3}>
                  <div className={styles.productFormField}>
                    <label htmlFor="productPrice">Ціна *</label>
                    <input
                      type="number"
                      id="productPrice"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      min="0"
                      required
                    />
                  </div>
                  <div className={styles.productFormField}>
                    <label htmlFor="productOldPrice">Стара ціна</label>
                    <input
                      type="number"
                      id="productOldPrice"
                      value={formData.oldPrice}
                      onChange={(e) => setFormData({ ...formData, oldPrice: e.target.value })}
                      placeholder="Вище поточної"
                      min="0"
                    />
                  </div>
                  <div className={styles.productFormField}>
                    <label htmlFor="productQuantity">Кількість на складі</label>
                    <input
                      type="number"
                      id="productQuantity"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      min="0"
                    />
                  </div>
                </div>
                <div className={styles.productFormField}>
                  <label htmlFor="productStockLabel">Статус наявності</label>
                  <select
                    id="productStockLabel"
                    value={formData.stockLabel}
                    onChange={(e) => setFormData({ ...formData, stockLabel: e.target.value })}
                  >
                    {STOCK_LABEL_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.adminLabel}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.productFormField}>
                  <label>Оптові ціни</label>
                  <div className={styles.productFormTableWrap}>
                  <table className={styles.productFormTable}>
                    <thead>
                      <tr>
                        <th>К-сть от, шт.</th>
                        <th>Ціна за ед., ₴</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {wholesaleTiers.map((tier, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="number"
                              value={tier.minQty}
                              onChange={(e) => updateWholesaleTier(index, 'minQty', e.target.value)}
                              placeholder="від 2"
                              min="2"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={tier.price}
                              onChange={(e) => updateWholesaleTier(index, 'price', e.target.value)}
                              placeholder="Ціна"
                              min="0"
                            />
                          </td>
                          <td>
                            <button type="button" className={styles.productFormRemoveBtn} onClick={() => removeWholesaleTier(index)}>
                              Видалити
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  <div className={styles.productFormTierActions}>
                    <button type="button" className={styles.productFormAddBtn} onClick={addWholesaleTier}>
                      + Додати рівень
                    </button>
                  </div>
                  <small>Оптова ціна повинна бути нижчою за роздрібну. Минимум — від 2 шт.</small>
                </div>

                <div className={styles.productFormField}>
                  <label>Товари в подарок ({giftProducts.length}/{MAX_GIFTS})</label>
                  <button
                    type="button"
                    className={styles.productFormAddBtn}
                    onClick={() => setIsGiftModalOpen(true)}
                    disabled={giftProducts.length >= MAX_GIFTS}
                  >
                    Додати подарунок
                  </button>
                  {giftProducts.length > 0 && (
                    <div className={styles.productFormGiftList}>
                      {giftProducts.map((gift, index) => (
                        <div key={`gift-${index}-${gift.id}`} className={styles.productFormGiftItem}>
                          <div className={styles.productFormGiftImage}>
                            {gift.images?.[0] ? (
                              <img src={assetUrl(gift.images[0])} alt={gift.title} />
                            ) : null}
                          </div>
                          <div className={styles.productFormGiftInfo}>
                            <div className={styles.productFormGiftTitle}>{gift.title}</div>
                          </div>
                          <button type="button" className={styles.productFormRemoveBtn} onClick={() => removeGift(index)}>
                            Видалити
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.productFormField}>
                  <label>Супутні товари ({relatedProducts.length}/{MAX_RELATED})</label>
                  <button
                    type="button"
                    className={styles.productFormAddBtn}
                    onClick={() => setIsRelatedModalOpen(true)}
                    disabled={relatedProducts.length >= MAX_RELATED}
                  >
                    Додати супутній товар
                  </button>
                  {relatedProducts.length > 0 && (
                    <div className={styles.productFormGiftList}>
                      {relatedProducts.map((related) => (
                        <div key={related.id} className={styles.productFormGiftItem}>
                          <div className={styles.productFormGiftImage}>
                            {related.images?.[0] ? (
                              <img src={assetUrl(related.images[0])} alt={related.title} />
                            ) : null}
                          </div>
                          <div className={styles.productFormGiftInfo}>
                            <div className={styles.productFormGiftTitle}>{related.title}</div>
                            {related.category?.name && (
                              <div className={styles.productFormRelatedCategory}>{related.category.name}</div>
                            )}
                          </div>
                          <button type="button" className={styles.productFormRemoveBtn} onClick={() => removeRelated(related.id)}>
                            Видалити
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <small>Можна обрати товари з будь-якої категорії. Якщо не обрано — на сайті підбираються автоматично</small>
                </div>
              </div>
            </section>

            <section className={styles.productFormSection}>
              <h3 className={styles.productFormSectionTitle}>Опис</h3>
              <div className={styles.productFormSectionContent}>
                <RichTextEditor
                  editorKey={product?.id ? `product-${product.id}` : 'product-new'}
                  value={formData.description}
                  onChange={(description) => setFormData((prev) => ({ ...prev, description }))}
                  placeholder="Повний опис товару"
                />
              </div>
            </section>

            <section className={styles.productFormSection}>
              <h3 className={styles.productFormSectionTitle}>Характеристики</h3>
              <div className={styles.productFormSectionContent}>
                <table className={styles.productFormTable}>
                  <thead>
                    <tr>
                      <th>Назва</th>
                      <th>Значення</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {attributes.map((row, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) => updateAttribute(index, 'name', e.target.value)}
                            placeholder="Наприклад: Матеріал"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={row.value}
                            onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                            placeholder="Значення"
                          />
                        </td>
                        <td>
                          <button type="button" className={styles.productFormRemoveBtn} onClick={() => removeAttribute(index)}>
                            Видалити
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button type="button" className={styles.productFormAddBtn} onClick={addAttribute}>
                  + Додати характеристику
                </button>
              </div>
            </section>

            <section className={styles.productFormSection}>
              <h3 className={styles.productFormSectionTitle}>SEO і снипет</h3>
              <div className={styles.productFormSectionContent}>
                <p className={styles.productFormSeoHint}>
                  Снипет — короткий текст для Google та інших пошуковиків (meta description).
                </p>

                <div className={styles.productFormField}>
                  <div className={styles.productFormFieldHead}>
                    <label htmlFor="productMetaTitle">Заголовок у пошуку (meta title)</label>
                    <button
                      type="button"
                      className={styles.productFormGenerateBtn}
                      onClick={() => {
                        const generated = generateProductMetaTitle({
                          title: formData.title,
                          article: formData.article,
                        });
                        setFormData((prev) => ({ ...prev, metaTitle: generated }));
                      }}
                    >
                      Згенерувати
                    </button>
                  </div>
                  <input
                    type="text"
                    id="productMetaTitle"
                    value={formData.metaTitle}
                    onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                    maxLength={SNIPPET_LIMITS.title}
                    placeholder={formData.title || 'Назва товару'}
                  />
                  <small>{formData.metaTitle.length}/{SNIPPET_LIMITS.title} символов</small>
                </div>

                <div className={styles.productFormField}>
                  <div className={styles.productFormFieldHead}>
                    <label htmlFor="productMetaDescription">Сниппет (meta description)</label>
                    <button
                      type="button"
                      className={styles.productFormGenerateBtn}
                      onClick={() => {
                        const category = categories.find((c) => String(c.id) === String(formData.categoryId));
                        const generated = generateProductSnippet({
                          title: formData.title,
                          description: formData.description,
                          article: formData.article,
                          price: formData.price,
                          categoryName: category?.name || '',
                        });
                        setFormData((prev) => ({ ...prev, metaDescription: generated }));
                      }}
                    >
                      Згенерувати з описания
                    </button>
                  </div>
                  <textarea
                    id="productMetaDescription"
                    value={formData.metaDescription}
                    onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                    rows={3}
                    maxLength={SNIPPET_LIMITS.description}
                    placeholder="Короткий опис для пошукової видачі"
                  />
                  <small>{formData.metaDescription.length}/{SNIPPET_LIMITS.description} символов</small>
                </div>

                <div className={styles.productFormField}>
                  <label htmlFor="productMetaKeywords">Meta keywords</label>
                  <input
                    type="text"
                    id="productMetaKeywords"
                    value={formData.metaKeywords}
                    onChange={(e) => setFormData({ ...formData, metaKeywords: e.target.value })}
                  />
                </div>

                <div className={styles.productFormSeoPreviewWrap}>
                  <small className={styles.categoryFormLangLabel}>Превʼю в пошуку</small>
                  <SeoPreview
                    title={formData.metaTitle || formData.title}
                    url={`${getSiteOrigin()}/products/${product?.id || 'new'}`}
                    description={
                      formData.metaDescription
                      || generateProductSnippet({
                        title: formData.title,
                        description: formData.description,
                        article: formData.article,
                        price: formData.price,
                        categoryName: categories.find((c) => String(c.id) === String(formData.categoryId))?.name || '',
                      })
                    }
                  />
                </div>
              </div>
            </section>
          </div>

          <aside className={styles.productFormMedia}>
            <div className={styles.productFormPreview}>
              {previewItem ? (
                <img src={getPreviewSrc(previewItem)} alt="Превʼю товару" />
              ) : (
                <div className={styles.productFormNoPreview}>Немає зображення</div>
              )}
            </div>
            <div className={styles.productFormThumbList}>
              {allImageItems.map((item, index) => (
                <div key={`${item.type}-${item.index}`} className={styles.productFormThumbItem}>
                  <button
                    type="button"
                    className={styles.productFormThumbBtn}
                    onClick={() => setPreviewIndex(index)}
                  >
                    <img src={getPreviewSrc(item)} alt={`Фото ${index + 1}`} />
                  </button>
                  <span className={styles.productFormThumbLabel}>
                    {item.type === 'existing' ? 'Поточне' : 'Нове'} {index + 1}
                  </span>
                  <button type="button" className={styles.productFormRemoveBtn} onClick={() => removeImageItem(index)}>
                    Видалити
                  </button>
                </div>
              ))}
            </div>
            <label className={styles.productFormUploadBtn}>
              Завантажити фото ({totalImages}/{MAX_IMAGES})
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleAddImages}
                disabled={slotsLeft <= 0}
              />
            </label>
          </aside>
        </div>

        <ProductSelectModal
          products={products}
          isOpen={isGiftModalOpen}
          onClose={() => setIsGiftModalOpen(false)}
          onSelect={handleAddGift}
          variant="gift"
          excludeProductId={product?.id || null}
          excludeIds={[]}
          title="Оберіть товар у подарунок"
        />

        <ProductSelectModal
          products={products}
          categories={categories}
          isOpen={isRelatedModalOpen}
          onClose={() => setIsRelatedModalOpen(false)}
          onSelect={handleAddRelated}
          variant="related"
          excludeProductId={product?.id || null}
          excludeIds={relatedProducts.map((item) => item.id)}
          title="Оберіть супутній товар"
        />

      </form>
    </div>
  );
};

export default ProductForm;
